import { useState } from 'react';
import { useSWRConfig } from 'swr';

import myShopClient from '@services/nearlive';
import { useVerifyEmail } from '@/components/verification/VerifyEmailProvider';

import { activateItemOptimistically, deactivateItemOptimistically } from './useInvalidateLocalPotential';

/**
 * Channel enable / disable / register-interest actions for a shop.
 *
 * Mirrors the retailers-dashboard `@services/channels` pattern (POST to the
 * per-shop channel endpoints) but calls the API directly through
 * `myShopClient` so unify doesn't pull in the shared service's `ShopProvider`
 * / `@nearst/ui` dependency chain.
 *
 * Each action revalidates the `useChannels` SWR cache (`['unify:channels',
 * shopId]`) so the page reflects the new status without a manual refresh.
 * In-flight channel types are tracked in a Set, so `isBusy(type)` lets each
 * button independently show a spinner even if two actions overlap.
 *
 * Enable / disable also POST to `/api/billing/channels/{shopId}/{type}/...` so
 * Unify customers get the matching Stripe sub item created / removed
 * alongside the channels-{stage} write (NEA-5591). The billing endpoint
 * 400s with "No active Unify subscription" for non-Unify customers, which we
 * swallow — billing is opportunistic, not load-bearing for the channel state.
 */
export function useChannelActions(shopId) {
	const { mutate } = useSWRConfig();
	const { requireVerifiedEmail } = useVerifyEmail();
	const [busyTypes, setBusyTypes] = useState(() => new Set());
	const [error, setError] = useState(null);

	const applyUnifyBilling = async (client, type, path) => {
		try {
			await client.post(`/api/billing/channels/${shopId}/${type}/${path}`);
			await mutate(['billing-status', shopId]);
		} catch (err) {
			// Non-Unify customer (400 'No active Unify subscription') or known
			// matrix rejection — log and continue. The channels-{stage} write
			// above has already taken effect; billing is best-effort.
			const status = err?.response?.status;
			const message = err?.response?.data || err?.message;
			if (status !== 400) {
				// eslint-disable-next-line no-console
				console.warn('Unify billing update failed', { type, path, status, message });
			}
		}
	};

	const run = async (type, path) => {
		setBusyTypes((prev) => new Set(prev).add(type));
		setError(null);
		try {
			const client = await myShopClient();
			const { data } = await client.post(`/api/channels/${shopId}/${type}/${path}`);
			const togglesScore = path === 'enable' || path === 'disable';
			if (togglesScore) {
				await applyUnifyBilling(client, type, path);
				// Optimistic Local Potential gauge bump — the resolver returns per-item
				// uplifts in `groups[]`, so we predict the new score and the gauge moves
				// on click; the revalidation below settles it on the server value.
				const updater = path === 'enable' ? activateItemOptimistically : deactivateItemOptimistically;
				await mutate(['local-potential', shopId], (current) => updater(current, type), { revalidate: false });
			}
			await Promise.all([mutate(['unify:channels', shopId]), ...(togglesScore ? [mutate(['local-potential', shopId])] : [])]);
			return data;
		} catch (err) {
			setError(err);
			throw err;
		} finally {
			setBusyTypes((prev) => {
				const next = new Set(prev);
				next.delete(type);
				return next;
			});
		}
	};

	// Wizard channels (Meta, Reservations, Local Checkout) need to seed the
	// channel record with a `pending` status and a config body before the user
	// finishes the setup flow — this is what the create handler at
	// POST /api/channels/{shopId}/{type} expects (a body, no /enable suffix).
	const create = async (type, body) => {
		setBusyTypes((prev) => new Set(prev).add(type));
		setError(null);
		try {
			const client = await myShopClient();
			const { data } = await client.post(`/api/channels/${shopId}/${type}`, body);
			await mutate(['unify:channels', shopId]);
			return data;
		} catch (err) {
			setError(err);
			throw err;
		} finally {
			setBusyTypes((prev) => {
				const next = new Set(prev);
				next.delete(type);
				return next;
			});
		}
	};

	// Patch a single channel's config without changing status — used by the
	// Meta wizard to store the domain verification code between steps.
	const update = async (type, config) => {
		setBusyTypes((prev) => new Set(prev).add(type));
		setError(null);
		try {
			const client = await myShopClient();
			const { data } = await client.patch(`/api/channels/${shopId}/${type}/update`, config);
			await mutate(['unify:channels', shopId]);
			return data;
		} catch (err) {
			setError(err);
			throw err;
		} finally {
			setBusyTypes((prev) => {
				const next = new Set(prev);
				next.delete(type);
				return next;
			});
		}
	};

	return {
		// Going live on any channel requires a verified email (NEA-5773) — gate
		// the canonical enable action so every "make it live" path (storefront,
		// channel detail, the Meta wizard's final step) is covered, not just the
		// "go live" CTA. Unverified → opens the verify dialog and runs the enable
		// on success; verified → enables immediately (promise passed through).
		enable: (type) => requireVerifiedEmail(() => run(type, 'enable')),
		disable: (type) => run(type, 'disable'),
		registerInterest: (type) => run(type, 'register-interest'),
		create,
		update,
		isBusy: (type) => busyTypes.has(type),
		error
	};
}
